import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { aufgabenStatus } from "@/lib/status";
import { fmtDate, daysLabel, fmtEUR } from "@/lib/format";
import { RiskBadge, HealthBar, TrendPill, SignalBadge } from "@/components/StatusBits";
import { Users, Activity, AlertTriangle, Flame, PhoneOff, ClipboardCheck, ArrowRight, Loader2, Upload, Sparkles, GitMerge, Siren, UserCheck, PhoneIncoming, AlarmClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Settings2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function begruessung(name) {
  const h = new Date().getHours();
  const gruss = h < 11 ? "Guten Morgen" : h < 18 ? "Guten Tag" : "Guten Abend";
  const vorname = (name || "").split(" ")[0];
  return vorname ? `${gruss}, ${vorname}` : gruss;
}

// Farbe je Kennzahl: Sinnbild im getoenten Kreis, Zahl in der Hausschrift.
const KPI_TON = {
  navy: "bg-slate-100 text-slate-700",
  gruen: "bg-emerald-50 text-emerald-600",
  orange: "bg-orange-50 text-orange-600",
  rot: "bg-red-50 text-red-600",
  grau: "bg-slate-100 text-slate-500",
  blau: "bg-blue-50 text-blue-600",
};

function Kpi({ icon: Icon, label, value, ton = "navy", testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${KPI_TON[ton]}`}>
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-xs font-semibold text-slate-500 leading-tight">{label}</span>
      </div>
      <div className="font-marke text-[32px] leading-none font-bold tabular text-slate-900">{value ?? "—"}</div>
    </div>
  );
}

// Die Bausteine des Dashboards. Jeder waehlt, was fuer seine Arbeit
// zaehlt - die Geschaeftsfuehrung braucht anderes als der Kundenservice.
const KACHELN = [
  { id: "kennzahlen", label: "Kennzahlen-Reihe", hinweis: "Kunden, Aktivität, Risiko, Follow-ups" },
  { id: "fruehwarnung", label: "Frühwarnung", hinweis: "Gefährdete aktive Kunden (nur mit Umsatzrecht)" },
  { id: "arbeit", label: "Tagesarbeit", hinweis: "Wer macht was, Überfälliges, offene Rückrufe" },
  { id: "zuordnung", label: "Umsatz-Zuordnung", hinweis: "Nicht zugeordnete Umsätze (nur mit Umsatzrecht)" },
  { id: "aufmerksamkeit", label: "Kunden mit Handlungsbedarf", hinweis: "Die Dringlichkeits-Liste" },
];

const VORLAGEN = {
  geschaeftsfuehrung: { label: "Geschäftsführung",
    kacheln: ["kennzahlen", "fruehwarnung", "arbeit", "zuordnung", "aufmerksamkeit"] },
  kundenservice: { label: "Kundenservice",
    kacheln: ["kennzahlen", "arbeit", "aufmerksamkeit"] },
  vertrieb: { label: "Vertrieb",
    kacheln: ["kennzahlen", "fruehwarnung", "zuordnung", "aufmerksamkeit"] },
};

/** Anpassen: Kacheln an/aus, Vorlage je Aufgabenbereich. Den Startbildschirm
 *  waehlt niemand mehr selbst: Beim Oeffnen kommt fuer alle das Wallboard
 *  (Franklin, 24.09.2026). */
function DashboardAnpassen({ user, setUser, onZu }) {
  const [kacheln, setKacheln] = useState(
    user?.dashboard_kacheln || KACHELN.map((k) => k.id));
  const [speichert, setSpeichert] = useState(false);

  const umschalten = (id) => setKacheln((alt) =>
    alt.includes(id) ? alt.filter((x) => x !== id) : [...alt, id]);

  const speichern = async () => {
    setSpeichert(true);
    try {
      await api.put("/meine-einstellungen", { dashboard_kacheln: kacheln });
      setUser({ ...user, dashboard_kacheln: kacheln });
      toast.success("Gespeichert – dein Dashboard.");
      onZu();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onZu()}>
      <DialogContent className="max-w-lg" data-testid="dashboard-anpassen">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-4 w-4 text-slate-400" /> Dashboard anpassen
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-xs font-medium text-slate-500 mb-1.5">
              Vorlage für deinen Aufgabenbereich
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {Object.entries(VORLAGEN).map(([id, v]) => (
                <button key={id} onClick={() => setKacheln(v.kacheln)}
                        data-testid={`vorlage-${id}`}
                        className="text-xs rounded-md border border-slate-200 px-2.5 py-1.5
                                   text-slate-600 hover:border-slate-900 hover:text-slate-900">
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="text-xs font-medium text-slate-500">Kacheln</div>
            {KACHELN.map((k) => (
              <label key={k.id} data-testid={`kachel-${k.id}`}
                     className="flex items-start gap-2.5 rounded-md border border-slate-200
                                px-3 py-2 cursor-pointer hover:border-slate-400">
                <input type="checkbox" checked={kacheln.includes(k.id)}
                       onChange={() => umschalten(k.id)} className="mt-0.5" />
                <span>
                  <span className="text-sm font-medium text-slate-900 block">{k.label}</span>
                  <span className="text-xs text-slate-400">{k.hinweis}</span>
                </span>
              </label>
            ))}
          </div>

          <p className="text-xs text-slate-400" data-testid="start-hinweis">
            Beim Öffnen des Programms erscheint für alle zuerst das Wallboard.
          </p>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onZu}>Abbrechen</Button>
            <Button onClick={speichern} disabled={speichert} data-testid="anpassen-speichern">
              {speichert && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              Speichern
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [anpassen, setAnpassen] = useState(false);
  // Ohne eigene Wahl: alles zeigen. Mit Wahl: nur die gewaehlten Kacheln.
  const kacheln = user?.dashboard_kacheln || KACHELN.map((k) => k.id);
  const zeigt = (id) => kacheln.includes(id);
  const [kpis, setKpis] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [aiStatus, setAiStatus] = useState(null);
  const [atRisk, setAtRisk] = useState(null);
  const [arbeit, setArbeit] = useState(null);

  const load = async () => {
    const [k, q, a] = await Promise.all([
      api.get("/dashboard/kpis"),
      api.get("/dashboard/attention-queue"),
      api.get("/dashboard/arbeit").catch(() => ({ data: null })),
    ]);
    setKpis(k.data);
    setQueue(Array.isArray(q.data) ? q.data : []);
    setArbeit(a.data);
  };

  useEffect(() => {
    (async () => {
      try {
        await load();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Frühwarnung: gefährdete aktive Kunden (nur für Nutzer mit Umsatz-Berechtigung)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/analytics/at-risk", { params: { months: 6 } });
        setAtRisk(res.data);
      } catch { /* keine Umsatz-Berechtigung o. Ä. – Kachel wird nicht gezeigt */ }
    })();
  }, []);

  // KI-Hintergrund-Analyse: Status pollen, solange sie läuft
  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const s = (await api.get("/ai/status")).data;
        setAiStatus(s);
        if (s?.status === "running") timer = setTimeout(poll, 4000);
      } catch { /* ignore */ }
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  const runBulk = async () => {
    setBulkBusy(true);
    toast.info("KI analysiert auffällige Kunden…");
    try {
      const res = await api.post("/ai/analyze-bulk");
      toast.success(`${res.data.analyzed} analysiert · ${res.data.skipped} unverändert · ${res.data.failed} fehlgeschlagen`);
    } catch {
      toast.error("Bulk-Analyse fehlgeschlagen.");
    } finally {
      setBulkBusy(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const empty = !kpis || kpis.total_customers === 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{begruessung(user?.name)}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {new Date().toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })}
            {kpis?.reference_date && ` · Analyse-Stichtag: ${fmtDate(kpis.reference_date)}`}
          </p>
          {aiStatus?.status === "running" && (
            <span data-testid="ai-running-badge" className="inline-flex items-center gap-1.5 mt-2 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
              <Loader2 className="h-3 w-3 animate-spin" /> KI analysiert auffällige Kunden im Hintergrund…
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button data-testid="bulk-analyze-button" onClick={runBulk} disabled={bulkBusy} variant="outline" className="h-10 md:h-9 flex-1 sm:flex-none">
            {bulkBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2 text-blue-500" />}
            Auffällige analysieren
          </Button>
          <Button data-testid="goto-import-button" onClick={() => navigate("/import")} variant="outline" className="h-10 md:h-9 flex-1 sm:flex-none">
            <Upload className="h-4 w-4 mr-2" /> CSV importieren
          </Button>
          <Button variant="outline" onClick={() => setAnpassen(true)}
                  data-testid="dashboard-anpassen-knopf"
                  title="Kacheln und Startbildschirm wählen"
                  className="h-10 md:h-9">
            <Settings2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {empty ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center">
          <PhoneOff className="h-8 w-8 text-slate-300 mx-auto" />
          <p className="text-sm text-slate-600 mt-3">Noch keine Kundendaten vorhanden.</p>
          <Button className="mt-4 h-9" onClick={() => navigate("/import")}>Erste CSV importieren</Button>
        </div>
      ) : (
        <>
          {zeigt("kennzahlen") && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Kpi icon={Users} label="Kunden gesamt" value={kpis.total_customers} testid="kpi-total" />
            <Kpi icon={Activity} label="Aktiv (30 T.)" value={kpis.active_30d} ton="gruen" testid="kpi-active" />
            <Kpi icon={AlertTriangle} label="Erhöhtes Risiko" value={kpis.elevated_risk} ton="orange" testid="kpi-elevated" />
            <Kpi icon={Flame} label="Kritisch" value={kpis.critical} ton="rot" testid="kpi-critical" />
            <Kpi icon={PhoneOff} label="Unbekannte Kontakte" value={kpis.unknown_contacts} ton="grau" testid="kpi-unknown" />
            <Kpi icon={ClipboardCheck} label="Offene Follow-ups" value={kpis.open_followups} ton="blau" testid="kpi-followups" />
          </div>
          )}

          {zeigt("fruehwarnung") && atRisk?.count > 0 && (
            <button onClick={() => navigate("/abwanderung")} data-testid="dashboard-atrisk-tile"
              className="w-full text-left bg-white border border-red-200 rounded-md overflow-hidden hover:border-red-300 transition-colors">
              <div className="px-4 py-3 border-b border-red-100 bg-red-50/60 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <Siren className="h-5 w-5 text-red-500 shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Frühwarnung: gefährdete aktive Kunden</div>
                    <div className="text-xs text-slate-500">
                      {atRisk.count} aktive Kunden zeigen Abwanderungs-Frühsignale · <b className="text-red-600">{atRisk.high} mit hohem Risiko</b>
                    </div>
                  </div>
                </div>
                <span className="text-xs text-red-700 font-medium inline-flex items-center gap-1 shrink-0">
                  Alle ansehen <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
              <div className="divide-y divide-slate-100">
                {(atRisk.items || []).slice(0, 5).map((it) => (
                  <div key={it.customer_id} data-testid={`dashboard-atrisk-row-${it.customer_id}`}
                    onClick={(e) => { e.stopPropagation(); navigate(`/kunden/${it.customer_id}`); }}
                    className="px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-slate-50">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{it.name}</div>
                      <div className="text-xs text-slate-400 truncate">{it.reasons?.slice(0, 2).join(" · ")}</div>
                    </div>
                    <span className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full border ${it.level === "hoch" ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                      {it.level}
                    </span>
                  </div>
                ))}
              </div>
            </button>
          )}

          {/* Wer arbeitet gerade woran, was ist überfällig, welche Rückrufe
              stehen aus – die Tagesfrage "Wo stehen wir?" ohne Klickstrecke. */}
          {zeigt("arbeit") && arbeit && (arbeit.personen?.length > 0 || arbeit.offene_rueckrufe?.length > 0) && (
            <div className="grid lg:grid-cols-3 gap-4" data-testid="dashboard-arbeit">
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-blue-500" />
                  <h2 className="text-sm font-semibold text-slate-900">In Arbeit – wer macht was</h2>
                </div>
                {!arbeit.personen?.length ? (
                  <div className="px-4 py-6 text-sm text-slate-400">Keine offenen Maßnahmen.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {(arbeit.personen || []).slice(0, 6).map((p) => (
                      <div key={p.owner} className="px-4 py-2.5 flex items-center justify-between gap-2 text-sm">
                        <span className="font-medium text-slate-900 truncate">{p.owner}</span>
                        <span className="text-xs text-slate-500 tabular shrink-0">
                          {p.in_arbeit > 0 && <b className="text-blue-600">{p.in_arbeit} in Arbeit</b>}
                          {p.in_arbeit > 0 && " · "}{p.offen} offen
                          {p.ueberfaellig > 0 && <> · <b className="text-red-600">{p.ueberfaellig} überfällig</b></>}
                        </span>
                      </div>
                    ))}
                    {(arbeit.in_arbeit || []).slice(0, 4).map((f) => (
                      <button key={f.followup_id} onClick={() => navigate(`/kunden/${f.customer_id}`)}
                              className="w-full text-left px-4 py-2 hover:bg-slate-50">
                        <div className="text-xs text-slate-700 truncate">{f.title} · <b>{f.customer_name}</b></div>
                        <div className="text-[11px] text-slate-400">{f.owner}{f.due_date ? ` · fällig ${fmtDate(f.due_date)}` : ""}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlarmClock className="h-4 w-4 text-red-500" />
                    <h2 className="text-sm font-semibold text-slate-900">Überfällige Maßnahmen</h2>
                  </div>
                  <button onClick={() => navigate("/erinnerungen")} className="text-xs text-slate-400 hover:text-slate-900 inline-flex items-center gap-1">
                    Alle <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
                {!arbeit.ueberfaellig?.length ? (
                  <div className="px-4 py-6 text-sm text-slate-400">Nichts überfällig.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {(arbeit.ueberfaellig || []).slice(0, 6).map((f) => (
                      <button key={f.followup_id} onClick={() => navigate(`/kunden/${f.customer_id}`)}
                              className="w-full text-left px-4 py-2.5 hover:bg-slate-50">
                        <div className="text-sm text-slate-900 truncate">{f.customer_name}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {f.title} · {f.owner} · <span className="text-red-600 font-medium">{Math.abs(f.days_diff)} Tage überfällig</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PhoneIncoming className="h-4 w-4 text-amber-500" />
                    <h2 className="text-sm font-semibold text-slate-900">Offene Rückrufe</h2>
                  </div>
                  <span className="text-xs text-slate-400 tabular">{arbeit.offene_rueckrufe_gesamt}</span>
                </div>
                {!arbeit.offene_rueckrufe?.length ? (
                  <div className="px-4 py-6 text-sm text-slate-400">Keine offenen Rückrufe.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {(arbeit.offene_rueckrufe || []).slice(0, 6).map((r, i) => (
                      <button key={i}
                              onClick={() => r.customer_id && navigate(`/kunden/${r.customer_id}`)}
                              className={`w-full text-left px-4 py-2.5 ${r.customer_id ? "hover:bg-slate-50" : "cursor-default"}`}>
                        <div className="text-sm text-slate-900 truncate">{r.name}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {r.betreff || "Rückruf offen"} · {fmtDate(r.occurred_at)}{r.zustaendig ? ` · ${r.zustaendig}` : ""}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {zeigt("zuordnung") && kpis.unassigned_revenue > 0 && (
            <button onClick={() => navigate("/zuordnung")} data-testid="unassigned-revenue-banner"
              className="w-full text-left bg-white border border-amber-200 rounded-md p-4 flex items-center justify-between hover:bg-amber-50/50 transition-colors">
              <div className="flex items-center gap-3">
                <GitMerge className="h-5 w-5 text-amber-500 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-slate-900 tabular">
                    {fmtEUR(kpis.unassigned_revenue)} Umsatz keinem Telefonkunden zugeordnet
                  </div>
                  <div className="text-xs text-slate-500">
                    {kpis.unassigned_revenue_count} Umsatz-Kunden ohne Verknüpfung · {kpis.unassigned_revenue_pct}% vom Gesamtumsatz
                  </div>
                </div>
              </div>
              <span className="text-xs text-amber-700 font-medium inline-flex items-center gap-1 shrink-0">
                Zuordnung prüfen <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </button>
          )}

          {zeigt("aufmerksamkeit") && (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Kunden, die Aufmerksamkeit benötigen</h2>
                <p className="text-xs text-slate-500">Sortiert nach Dringlichkeit</p>
              </div>
              <span className="text-xs text-slate-400 tabular">{queue.length} Kunden</span>
            </div>

            {queue.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500">
                Alle Kunden im stabilen Bereich. Kein Handlungsbedarf.
              </div>
            ) : (
              <>
              {/* Telefon: Karten – acht Spalten seitwärts zu schieben ist
                  keine Bedienung. */}
              <div className="md:hidden divide-y divide-slate-100" data-testid="queue-cards">
                {queue.map((c) => (
                  <button key={c.customer_id} data-testid={`queue-card-${c.customer_id}`}
                          onClick={() => navigate(`/kunden/${c.customer_id}`)}
                          className="w-full text-left px-4 py-3 active:bg-slate-50">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">{c.name}</div>
                        <div className="text-xs text-slate-500 tabular mt-0.5">
                          {daysLabel(c.days_since_last)}{c.phones?.[0] ? ` · ${c.phones[0]}` : ""}
                        </div>
                      </div>
                      <RiskBadge risk={c.risk} />
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <HealthBar score={c.health_score} />
                      <TrendPill trend={c.trend} />
                    </div>
                    {c.reasons?.[0] && (
                      <div className="text-xs text-slate-500 mt-1.5 line-clamp-2">{c.reasons[0]}</div>
                    )}
                    {c.next_best_action?.title && (
                      <div className="text-xs font-medium text-slate-900 mt-1">→ {c.next_best_action.title}</div>
                    )}
                  </button>
                ))}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-[0.04em] text-slate-500 border-b border-slate-200">
                      <th className="px-4 py-2 font-semibold">Kunde</th>
                      <th className="px-4 py-2 font-semibold">Health</th>
                      <th className="px-4 py-2 font-semibold">Risiko</th>
                      <th className="px-4 py-2 font-semibold">Letzter Kontakt</th>
                      <th className="px-4 py-2 font-semibold">Trend</th>
                      <th className="px-4 py-2 font-semibold min-w-[280px]">Grund</th>
                      <th className="px-4 py-2 font-semibold min-w-[180px]">Empfohlene Maßnahme</th>
                      <th className="px-4 py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queue.map((c) => (
                      <tr
                        key={c.customer_id}
                        data-testid={`queue-row-${c.customer_id}`}
                        onClick={() => navigate(`/kunden/${c.customer_id}`)}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 flex items-center gap-1.5">
                            {c.name}
                            <ArrowRight className="h-3 w-3 text-slate-300" />
                          </div>
                          <div className="text-xs text-slate-400 tabular">{c.phones?.[0]}</div>
                          {c.business?.business_signal && c.business.business_signal !== "neutral" && (
                            <div className="mt-1"><SignalBadge signal={c.business.business_signal} /></div>
                          )}
                        </td>
                        <td className="px-4 py-3"><HealthBar score={c.health_score} /></td>
                        <td className="px-4 py-3"><RiskBadge risk={c.risk} /></td>
                        <td className="px-4 py-3 text-slate-600 tabular whitespace-nowrap">{daysLabel(c.days_since_last)}</td>
                        <td className="px-4 py-3"><TrendPill trend={c.trend} /></td>
                        <td className="px-4 py-3 text-slate-600">
                          <span className="line-clamp-2">{c.reasons?.[0]}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-slate-900 font-medium">{c.next_best_action?.title}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-500">{aufgabenStatus(c.followup_status)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
          )}
        </>
      )}

      {anpassen && (
        <DashboardAnpassen user={user} setUser={setUser}
                           onZu={() => setAnpassen(false)} />
      )}
    </div>
  );
}
